import { DeviceGroupVaultNotInitializedError } from '../../errors/device-group-vault/device-group-vault-not-initialized-error';
import { InvalidDateDeviceGroupVaultEntryError } from '../../errors/device-group-vault/invalid-date-device-group-vault-entry-error';
import { InvalidDeviceGroupVaultEntryError } from '../../errors/device-group-vault/invalid-device-group-vault-entry-error';
import { InvalidDeviceGroupVaultEntrySignatureError } from '../../errors/device-group-vault/invalid-device-group-vault-entry-signature-error';
import { addMinutes, areEqualUint8Array, combineUint8Arrays, getUnsafeDateNow } from '../../utils/utils';
import { getDeviceGroupVaultEntryBytesToBeSigned } from './device-group-utils';
import { DeviceGroupVaultOperation } from './device-group-vault-operation';
export class DeviceGroupValidationContext {
    constructor(entries, keyVault, allowEmpty, validateFromEntry) {
        this.entries = entries;
        this.keyVault = keyVault;
        this.allowEmpty = allowEmpty;
        this.validateFromEntry = validateFromEntry;
        this.supportedVaultEntryVersion = 1;
        this.minimumDate = new Date(Date.UTC(2022, 4, 23, 0, 0, 0));
        this.timeSkew = 5; // in minutes
        this.validatedEntries = entries.slice(0, validateFromEntry);
        this.validatedEffectiveEntries = [];
        this.validatedEntries.forEach(entry => {
            this.updateEffectiveEntries(entry);
        });
    }
    async validateAll() {
        if (this.entries.length === 0 && !this.allowEmpty) {
            throw new DeviceGroupVaultNotInitializedError();
        }
        for (const entry of this.entries.slice(this.validateFromEntry)) {
            await this.validate(entry);
        }
    }
    async validate(entry) {
        this.validateEntry(entry);
        this.validateOperation(entry);
        if (entry.entryId === 0) {
            this.validateRootEntry(entry);
        }
        else {
            this.validateNonRootEntry(entry);
        }
        await this.verifyEntrySignature(entry);
        this.validatedEntries.push(entry);
        this.updateEffectiveEntries(entry);
    }
    updateEffectiveEntries(entry) {
        if (entry.operation === DeviceGroupVaultOperation.Remove || entry.operation === DeviceGroupVaultOperation.Update) {
            const index = this.validatedEffectiveEntries.findIndex(e => entry.groupId === e.groupId, 0);
            if (index > -1) {
                this.validatedEffectiveEntries.splice(index, 1);
            }
        }
        if (entry.operation === DeviceGroupVaultOperation.Add || entry.operation === DeviceGroupVaultOperation.Update) {
            this.validatedEffectiveEntries.push(entry);
        }
    }
    validateEntry(entry) {
        if (entry.version !== this.supportedVaultEntryVersion) {
            throw new InvalidDeviceGroupVaultEntryError('Unsupported version.', entry);
        }
        if (entry.entryId !== this.validatedEntries.length) {
            throw new InvalidDeviceGroupVaultEntryError('Entry id mismatch.', entry);
        }
        if (entry.addedAt < this.minimumDate || entry.addedAt > addMinutes(getUnsafeDateNow(), this.timeSkew)) {
            throw new InvalidDateDeviceGroupVaultEntryError(entry);
        }
    }
    validateOperation(entry) {
        switch (entry.operation) {
            case DeviceGroupVaultOperation.NoOp:
                break;
            case DeviceGroupVaultOperation.Add:
                if (this.validatedEntries.some(e => e.groupId === entry.groupId && e.operation === DeviceGroupVaultOperation.Add)) {
                    throw new InvalidDeviceGroupVaultEntryError('Group id already exists in the vault.', entry);
                }
                break;
            case DeviceGroupVaultOperation.Remove:
                if (!this.validatedEffectiveEntries.some(e => e.groupId === entry.groupId)) {
                    throw new InvalidDeviceGroupVaultEntryError(`Group id can't be removed because it is not active.`, entry);
                }
                break;
            case DeviceGroupVaultOperation.Update:
                if (!this.validatedEffectiveEntries.some(e => e.groupId === entry.groupId)) {
                    throw new InvalidDeviceGroupVaultEntryError(`Group id can't be updated because it is not active.`, entry);
                }
                break;
            default:
                throw new InvalidDeviceGroupVaultEntryError('Unsupported operation.', entry);
        }
    }
    validateRootEntry(entry) {
        if (!areEqualUint8Array(entry.entrySignature, entry.blockchainSignature)) {
            throw new InvalidDeviceGroupVaultEntrySignatureError('BlockchainSignature', entry);
        }
        if (entry.operation !== DeviceGroupVaultOperation.NoOp) {
            throw new InvalidDeviceGroupVaultEntryError(`Root entry must have ${DeviceGroupVaultOperation.NoOp} operation.`, entry);
        }
    }
    validateNonRootEntry(entry) {
        if (entry.operation === DeviceGroupVaultOperation.NoOp) {
            throw new InvalidDeviceGroupVaultEntryError(`Non root entry must not have ${DeviceGroupVaultOperation.NoOp} operation.`, entry);
        }
        const previousEntry = this.entries[entry.entryId - 1];
        if (previousEntry.addedAt > entry.addedAt) {
            throw new InvalidDateDeviceGroupVaultEntryError(entry);
        }
        if (entry.vaultEntryId < 0) {
            throw new InvalidDeviceGroupVaultEntryError(`VaultEntryId is invalid.`, entry);
        }
    }
    async verifyEntrySignature(entry) {
        if (entry.entryId !== 0) {
            const previousEntry = this.validatedEntries[entry.entryId - 1];
            const blockchainSignaturePayload = combineUint8Arrays(previousEntry.blockchainSignature, entry.entrySignature);
            await this.keyVault.verifySignatureByIdAt(blockchainSignaturePayload, entry.blockchainSignature, 'BlockchainSignature', entry.addedAt, entry.vaultEntryId);
        }
        const entrySignaturePayload = getDeviceGroupVaultEntryBytesToBeSigned(entry);
        await this.keyVault.verifySignatureByIdAt(entrySignaturePayload, entry.entrySignature, 'EntrySignature', entry.addedAt, entry.vaultEntryId);
    }
}
